/**
 * PostChainPanel — post the steps before a milestone together (docs/COMPRESSED_CHAIN.md, Matt
 * 2026-10-29: "keep the milestones, fix the friction").
 *
 * On a small project the Conceptual Framework, its review, the IDP and its review can all happen in one
 * meeting ("I like it, keep going"). The milestones stay; this posts the unposted steps in front of the
 * one being posted, in order, on ONE date with ONE note, and points every Client Review among them at
 * the meeting whose posted minutes hold the approval, with the line that approved it. Each step is
 * recorded as "posted with" the milestone that triggered it, so the history reads as one meeting, not
 * four. Their open checklist items are marked not required with that same reason. Only the milestone
 * itself (posted next, through the normal window) can add anything to the Client Hub.
 *
 * Shown by PostMilestoneModal in place of "Not yet" when the milestone is waiting on predecessors.
 * Refuses a chain step that has to be posted on its own: a permit or revision submission, a set with
 * drawing sheets, or a plane with threads still riding it.
 */
import { useEffect, useMemo, useState } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { isStartedAhead } from '../outline/milestoneFlow.js'
import { postMilestone } from '../services/milestones.js'
import { getMilestoneChecklistItems, markChecklistItemNotRequired } from '../services/checklist.js'
import { getMilestoneSheetRevisions } from '../services/sheets.js'
import { getPassengersFor } from '../services/passengers.js'
import { getPostedMeetings } from '../services/meetings.js'
import { recordPost } from '../services/milestoneFlow.js'
import { POST_PERMIT_TYPE_IDS, POST_REVISION_TYPE_IDS, HIDDEN_CL_CATEGORIES } from '../outline/constants.js'
import { isSheetGateMilestone } from '../journey/eligibility.js'

export const CLIENT_REVIEW_TYPE_ID = 'recgP0EdHfdsI2MhJ'

function findMs(id) {
  const lists = [ctx.state && ctx.state.milestones]
  if (ctx.milestonesCache) for (const k of Object.keys(ctx.milestonesCache)) lists.push(ctx.milestonesCache[k])
  for (const l of lists) if (Array.isArray(l)) for (const m of l) if (m && m.id === id) return m
  return null
}
export const msTitle = (m) => String((m && m.fields && (m.fields['Full Deliverable Name'] || m.fields['Name'])) || 'Milestone').replace(/\s*🔄\s*/g, ' ').trim()
const typeOf = (m) => ((m && m.fields && m.fields['Deliverable']) || [])[0] || ''

/** The unposted steps in front of `ms`, earliest first (every open predecessor, recursively). */
export function earlierSteps(ms) {
  const out = [], seen = new Set()
  const visit = (m) => {
    for (const pid of (m.fields['Predecessor'] || [])) {
      if (seen.has(pid) || isStartedAhead(m.id, pid)) continue
      seen.add(pid)
      const p = findMs(pid)
      if (!p || p.fields['Posted Date']) continue
      visit(p)
      out.push(p)
    }
  }
  visit(ms)
  return out
}

/** The approving meeting: a posted meeting and the line in its minutes that approved the design. */
export function MeetingApprovalFields({ projectId, value, onChange, label = 'Approved in a meeting' }) {
  const [meetings, setMeetings] = useState(null)
  useEffect(() => { let on = true; getPostedMeetings(projectId).then(l => on && setMeetings(l)).catch(() => on && setMeetings([])); return () => { on = false } }, [projectId])
  const v = value || {}
  const field = { display: 'block', width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 6 }}>{label}</div>
      <select value={v.meetingId || ''} onChange={e => { const m = (meetings || []).find(x => x.id === e.target.value); onChange({ ...v, meetingId: m ? m.id : '', meetingLabel: m ? m.label : '' }) }} style={field}>
        <option value="">{meetings === null ? 'Loading meetings…' : meetings.length ? 'Not from a meeting' : 'No posted minutes on this project'}</option>
        {(meetings || []).map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
      </select>
      {v.meetingId && (
        <>
          <textarea value={v.quote || ''} onChange={e => onChange({ ...v, quote: e.target.value })} rows={2}
            placeholder="The line in the minutes that approved it, as written"
            style={{ ...field, marginTop: 6, resize: 'vertical' }} />
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 4, lineHeight: 1.4 }}>
            The client received these minutes with a date to ask for corrections, which makes them the written record. The whole design, not “they liked it.”
          </div>
        </>
      )}
    </div>
  )
}

export default function PostChainPanel({ ms, projectId, date, setDate, onPosted, onClose, btnBase }) {
  const steps = useMemo(() => earlierSteps(ms), [ms])
  const [problems, setProblems] = useState(null)   // null checking | [{ id, why }]
  const [openCount, setOpenCount] = useState({})
  const [note, setNote] = useState('')
  const [approval, setApproval] = useState({})
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const hasReview = steps.some(s => typeOf(s) === CLIENT_REVIEW_TYPE_ID) || typeOf(ms) === CLIENT_REVIEW_TYPE_ID

  useEffect(() => {
    let on = true
    ;(async () => {
      const probs = [], counts = {}
      let aboard = {}
      try { aboard = (await getPassengersFor(steps.map(s => s.id))).byMilestone || {} } catch { aboard = {} }
      for (const s of steps) {
        const t = typeOf(s)
        if (POST_PERMIT_TYPE_IDS.includes(t) || POST_REVISION_TYPE_IDS.includes(t)) { probs.push({ id: s.id, why: 'is a submission; post it on its own' }); continue }
        if ((aboard[s.id] || []).length) { probs.push({ id: s.id, why: 'has ' + aboard[s.id].length + ' thread' + (aboard[s.id].length === 1 ? '' : 's') + ' riding it; close or move them first' }); continue }
        let revs = []
        try { revs = await getMilestoneSheetRevisions(s.id) } catch { revs = [] }
        if (isSheetGateMilestone(t) || (revs || []).length) { probs.push({ id: s.id, why: 'carries drawing sheets; post it on its own' }); continue }
        try {
          const items = await getMilestoneChecklistItems(s.id)
          counts[s.id] = (items || []).filter(it => {
            const f = it.fields || {}
            const cat = (Array.isArray(f['Category (from Deliverable Checklist Item Template)']) ? f['Category (from Deliverable Checklist Item Template)'][0] : f['Category (from Deliverable Checklist Item Template)']) || 'Other'
            return HIDDEN_CL_CATEGORIES.indexOf(cat) === -1 && !f['Complete'] && !f['Milestone Not Required']
          }).map(it => it.id)
        } catch { counts[s.id] = [] }
      }
      if (on) { setProblems(probs); setOpenCount(counts) }
    })()
    return () => { on = false }
  }, [steps])

  const mainName = msTitle(ms)
  const ready = problems && !problems.length && steps.length && note.trim() && date
    && (!approval.meetingId || String(approval.quote || '').trim())

  async function postAll() {
    setBusy('Posting…'); setErr('')
    const f = (ctx.currentMember && ctx.currentMember.fields) || {}
    const me = { id: wf.currentMemberId || null, name: f['Member Name'] || f['Name'] || '' }
    // "Posted together with" the other steps of this go (or, for a lone step, the milestone it unblocks).
    const others = (s) => { const o = steps.filter(x => x.id !== s.id); return o.length ? { id: o[o.length - 1].id, name: o.map(msTitle).join(' and ') } : { id: ms.id, name: mainName } }
    try {
      for (const s of steps) {
        const w = others(s)
        const reason = 'Done in one meeting with ' + w.name + ': ' + note.trim()
        setBusy('Posting ' + msTitle(s) + '…')
        for (const itemId of openCount[s.id] || []) await markChecklistItemNotRequired(itemId, reason, me.id)
        const res = await postMilestone(s.id, { date })
        if (res && res.error) throw new Error(res.error)
        s.fields['Posted Date'] = date
        const isReview = typeOf(s) === CLIENT_REVIEW_TYPE_ID
        await recordPost({
          milestoneId: s.id, postedWithId: w.id, postedWithName: w.name, note: note.trim(),
          ...(isReview && approval.meetingId ? { meetingId: approval.meetingId, meetingLabel: approval.meetingLabel, quote: String(approval.quote || '').trim() } : {}),
          memberId: me.id, memberName: me.name,
        })
      }
      window.dispatchEvent(new Event('outline-render'))
      setBusy('')
      onPosted({ approval: typeOf(ms) === CLIENT_REVIEW_TYPE_ID && approval.meetingId ? approval : null, note: note.trim() })
    } catch (e) {
      setBusy(''); setErr('Stopped: ' + (e.message || 'a step failed to post') + '. The steps above it posted; open this again to finish.')
      window.dispatchEvent(new Event('outline-render'))
    }
  }

  const T = { fontFamily: '"DM Sans",sans-serif' }
  if (!steps.length) return null
  return (
    <div style={T}>
      <div style={{ fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 10 }}>Post the steps before it</div>
      <div style={{ fontSize: 13, color: 'var(--aj-text)', lineHeight: 1.5, marginBottom: 10 }}>
        {mainName} is waiting on {steps.length === 1 ? 'one step' : steps.length + ' steps'}. If {steps.length === 1 ? 'it happened in one meeting with the rest' : 'they happened together'}, post {steps.length === 1 ? 'it' : 'them in one go'} on the same date. {steps.length > 1 ? 'Each is marked as posted together with the others.' : ''}
      </div>
      <div style={{ border: '0.5px solid var(--aj-border)', borderRadius: 6, padding: '6px 10px', marginBottom: 6 }}>
        {steps.map((s, i) => {
          const p = (problems || []).find(x => x.id === s.id)
          const n = (openCount[s.id] || []).length
          return (
            <div key={s.id} style={{ display: 'flex', gap: 8, padding: '5px 0', borderTop: i ? '0.5px solid var(--aj-border)' : 'none', fontSize: 13 }}>
              <span style={{ fontFamily: '"DM Mono",monospace', color: 'var(--aj-text-muted)', width: 16 }}>{i + 1}</span>
              <span style={{ flex: 1, color: 'var(--aj-text)' }}>{msTitle(s)}
                {p && <span style={{ color: '#c0392b' }}> · {p.why}</span>}
                {!p && problems && n > 0 && <span style={{ color: 'var(--aj-text-muted)' }}> · {n} open checklist item{n === 1 ? '' : 's'} marked not required</span>}
              </span>
            </div>
          )
        })}
      </div>
      {problems === null && <div style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>Checking each step…</div>}

      <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', margin: '12px 0 6px' }}>Posted date, for all of them</div>
      <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontFamily: '"DM Mono",monospace', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }} />
      <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', margin: '12px 0 6px' }}>What happened</div>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="e.g. Presented plans and renderings in one meeting; the clients approved the design."
        style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', resize: 'vertical', fontFamily: '"DM Sans",sans-serif' }} />
      {hasReview && <MeetingApprovalFields projectId={projectId} value={approval} onChange={setApproval} />}

      {err && <div style={{ fontSize: 12.5, color: '#c0392b', marginTop: 10, lineHeight: 1.4 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} disabled={!!busy} style={{ ...btnBase, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)' }}>Close</button>
        <button onClick={postAll} disabled={!ready || !!busy} style={{ ...btnBase, background: '#07272D', color: '#fff', opacity: ready && !busy ? 1 : 0.45 }}>
          {busy || ('Post ' + (steps.length === 1 ? 'it' : 'these ' + steps.length))}
        </button>
      </div>
    </div>
  )
}
